import React, { useCallback, useMemo, memo } from "react";
import PropTypes from "prop-types";
import { Controller, useForm } from "react-hook-form";
import { Close, FilterList } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

import { ButtonV1, DatePickerV1, MultiSelectSearchDropdown } from "components";
import theme from "themeProvider";

import {
  getCompanyNamesFromSellerData,
  getMaxToDate,
  getUniqueStatesFromSellerData,
} from "../../makeData";

const palette = theme.palette;

const fieldLabelSx = {
  color: palette.grey[1000],
  fontSize: 14,
  lineHeight: "16px",
  mb: 0.5,
};

const controlSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    minHeight: 42,
    backgroundColor: palette.common.white,
  },
  "& .MuiOutlinedInput-input": {
    fontSize: 14,
  },
  "& .MuiInputBase-input::placeholder": {
    color: palette.grey[1000],
    opacity: 1,
  },
};

const sxPropType = PropTypes.oneOfType([
  PropTypes.array,
  PropTypes.func,
  PropTypes.object,
]);

const FieldLabel = React.memo(({ children, sx = {} }) => (
  <Typography component="label" sx={{ ...fieldLabelSx, ...sx }}>
    {children}
  </Typography>
));

FieldLabel.propTypes = {
  children: PropTypes.node.isRequired,
  sx: sxPropType,
};

const CustomTextField = memo(
  ({ name, label, control, digitsOnly = false, maxLength }) => {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            size="small"
            fullWidth
            label={label}
            inputProps={{ maxLength }}
            onChange={(e) => {
              const value = digitsOnly
                ? e.target.value.replace(/\D/g, "")
                : e.target.value;

              field.onChange(value);
            }}
          />
        )}
      />
    );
  },
);

const defaultFormValues = {
  sellerNames: [],
  sellerStates: [],
  soNumber: "",
  slo: "",
  dateFrom: "",
  dateTo: "",
};

const FilterModal = ({
  open,
  onClose,
  onSubmit,
  onReset,
  sellerData = [],
  appliedFilters,
}) => {
  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: appliedFilters || defaultFormValues,
  });

  const fromDate = watch("dateFrom");
  const sellerNameOptions = useMemo(
    () => getCompanyNamesFromSellerData(sellerData),
    [sellerData],
  );
  const sellerStateOptions = useMemo(
    () => getUniqueStatesFromSellerData(sellerData),
    [sellerData],
  );
  const maxToDate = useMemo(() => getMaxToDate(fromDate), [fromDate]);

  const handleReset = useCallback(() => {
    reset(defaultFormValues);
    onReset?.();
  }, [onReset, reset]);

  const handleApply = useCallback(
    (data) => {
      onSubmit?.(data);
      onClose?.();
    },
    [onClose, onSubmit],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 461,
          padding: 2,
          maxWidth: "calc(100vw - 32px)",
          borderRadius: 2,
          boxShadow: `0 20px 45px ${alpha(palette.common.black, 0.18)}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2.5,
          py: 4,
          color: palette.grey.A300,
          fontSize: 16,
          fontWeight: 700,
          lineHeight: "24px",
        }}
      >
        <FilterList sx={{ color: palette.grey.A300, fontSize: 18 }} />
        Filter Sellers
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 18,
            top: 17,
            width: 26,
            height: 26,
            border: `1px solid ${palette.divider}`,
            borderRadius: "6px",
            color: palette.text.secondary,
          }}
        >
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ borderColor: palette.divider }} />

      <DialogContent sx={{ px: 2.5, py: 2 }}>
        <Box component="form" onSubmit={handleSubmit(handleApply)}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <CustomTextField
                name="soNumber"
                label="So number"
                control={control}
              />
            </Grid>

            <Grid item xs={12}>
              <CustomTextField
                name="slo"
                label="SLO (mapped order)"
                control={control}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="sellerNames"
                control={control}
                render={({ field }) => (
                  <MultiSelectSearchDropdown
                    {...field}
                    label="Source seller"
                    selectAll
                    options={sellerNameOptions}
                    sx={controlSx}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="sellerStates"
                control={control}
                render={({ field }) => (
                  <MultiSelectSearchDropdown
                    {...field}
                    label="Seller states"
                    selectAll
                    options={sellerStateOptions}
                    sx={controlSx}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography
                sx={{px: 1, mb: 1 }}
              >
                SO created: 
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 32px 1fr",
                  alignItems: "center",
                  columnGap: 1,
                }}
              >
                <Controller
                  name="dateFrom"
                  control={control}
                  render={({ field }) => (
                    <DatePickerV1
                      value={field.value}
                      onChange={field.onChange}
                      disableFuture
                      label="From date"
                      inputProps={{ placeholder: "dd-mm-yyyy" }}
                      dateStyle={controlSx}
                    />
                  )}
                />

                <Typography
                  component="span"
                  sx={{
                    color: palette.grey[1000],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18,
                    lineHeight: 1,
                    width: 32,
                    height: 48,
                    textAlign: "center",
                  }}
                >
                  &#8594;
                </Typography>

                <Controller
                  name="dateTo"
                  control={control}
                  render={({ field }) => (
                    <DatePickerV1
                      value={field.value}
                      onChange={field.onChange}
                      disableFuture
                      label="To date"
                      minDate={fromDate}
                      maxDate={maxToDate}
                      inputProps={{ placeholder: "dd-mm-yyyy" }}
                      dateStyle={controlSx}
                    />
                  )}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <Divider sx={{ borderColor: palette.divider }} />

      <DialogActions
        sx={{ justifyContent: "flex-end", gap: 1, px: 2.5, py: 2, mt: 2 }}
      >
        <ButtonV1
          title="Clear all"
          variant="outlined"
          onClick={handleReset}
          color="secondary"
          style={{
            px: 6,
          }}
        />

        <ButtonV1
          title="Apply filters"
          variant="contained"
          onClick={handleSubmit(handleApply)}
          color="secondary"
          style={{
            px: 6,
          }}
        />
      </DialogActions>
    </Dialog>
  );
};

FilterModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  onReset: PropTypes.func,
  sellerData: PropTypes.arrayOf(PropTypes.object),
  customersData: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  ),
  fetchCustomersData: PropTypes.func,
  buyerLoading: PropTypes.bool,
};

export default React.memo(FilterModal);
